import React, { useRef, useState } from "react";
import submit from "submit-form";
import PropTypes from "prop-types";
import { ObjectId } from "bson";
import ToolTip from "../../../../parts/tooltip";
import SvgIcon from "../../../../parts/SvgIcon";
import Pagination from "../../../../parts/pagination";
import useClickOutside from "../../../../hooks/useClickOutside";

/**
 * Function to apply the custom height of the cards
 * @param {*} templates
 * @returns
 */
const getHeightClass = (templates) => {
  let maxLength = 0;
  for (const t of templates) {
    const title = t.title || "";
    const description = t.description || "";
    maxLength = Math.max(maxLength, (title + description).length);
  }

  if (maxLength > 120) return "h-[9.25rem]";
  if (maxLength > 107) return "h-[9rem]";
  return "h-[8.25rem]";
};

const MealPlanTemplates = ({ locals, templates }) => {
  const { query, mealPlanCount = 1 } = locals;
  const { sort = "createdDate" } = query;
  const dropdownRef = useRef(null);
  const sortFunctions = {
    name: (a, b) => a.title.localeCompare(b.title),
    nameDescending: (a, b) => b.title.localeCompare(a.title),
    createdDate: (a, b) =>
      new ObjectId(b._id).getTimestamp() - new ObjectId(a._id).getTimestamp(),
  };
  const heightClass = getHeightClass(templates);
  const sortedTemplates = [...templates].sort(
    sortFunctions[sort] || sortFunctions.createdDate
  );

  const [openOptions, setOpenOptions] = useState(null);

  const footerListIcon = [
    {
      id: `preview-template`,
      src: "/img/new-icons/eye.svg",
      alt: "preview",
      message: `Preview`,
      url: "/coaching-portal/templates/edit-meal-plan",
    },
    {
      id: "copy-meal-plan-to",
      src: "/img/new-icons/copy-to.svg",
      alt: "fetch",
      message: `Copy Template To`,
      url: "/actions/refresh?modal[type]=copy-meal-plan-to",
    },
    {
      id: "copy-meal-plan-from",
      src: "/img/new-icons/copy-from.svg",
      alt: "fetch",
      message: "Copy Template From",
      url: "/actions/refresh?modal[type]=copy-meal-plan-from",
    },
    {
      id: `meal-plan-settings`,
      src: "/img/new-icons/settings.svg",
      alt: "view",
      message: `Settings`,
      url: "/actions/refresh?modal[type]=handle-meal-plan",
    },
    {
      id: "delete-template",
      src: "/img/new-icons/red-trash.svg",
      alt: "delete",
      message: `Delete `,
      url: ``,
    },
  ];

  const cardListIcon = [
    {
      src: "/img/new-icons/duplicate.svg",
      alt: "duplicate",
      message: "Duplicate Week",
      id: "duplicate-week",
      url: `/actions/meal-planner/duplicate-week-meal-plan`,
    },
    {
      src: "/img/new-icons/red-trash.svg",
      alt: "delete",
      message: "Delete Week",
      id: "delete-week",
      url: ``,
    },
  ];

  /**
   * Function to show the details options in the correct position
   * @param {*} e
   * @param {*} templateId
   * @param {*} weekIndex
   */
  const handleOptions = (e, templateId, weekIndex) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();

    setOpenOptions((prev) =>
      prev?.templateId === templateId && prev?.weekIndex === weekIndex
        ? null
        : {
            templateId,
            weekIndex,
            position: {
              top: rect.bottom + window.scrollY,
              left: rect.left + window.scrollX,
            },
          }
    );
  };

  /**
   * Close the details options clicking outside
   */
  useClickOutside(
    dropdownRef,
    () => {
      setTimeout(() => {
        setOpenOptions(null);
      }, 100);
    },
    openOptions
  );

  /**
   * Function to delete a meal plan
   * @param {*} templateId
   */
  const handleDeleteTemplate = (templateId) => {
    if (
      process.browser &&
      confirm(`Are you sure you want to delete this meal plan?`)
    ) {
      submit(
        `/actions/meal-planner/delete-meal-plan?mealPlanId=${templateId}&proxyAthlete=${null}&nutritionPlanId=${null}`,
        {
          method: "POST",
        }
      );
    }
  };

  /**
   * Function to delete a week from the template
   * @param {*} templateId
   * @param {*} week
   */
  const handleDeleteWeek = (templateId, week) => {
    if (
      process.browser &&
      confirm(`Are you sure you want to delete this week?`)
    ) {
      submit(
        `/actions/meal-planner/delete-week-meal-plan?mealPlanId=${templateId}&week=${week}`,
        {
          method: "POST",
        }
      );
    }
  };

  /**
   * Function to duplicate a week from the template
   * @param {*} templateId
   * @param {*} week
   */
  const handleDuplicateWeek = (templateId, week) => {
    submit(
      `/actions/meal-planner/duplicate-week-meal-plan?mealPlanId=${templateId}&week=${week}`,
      {
        method: "POST",
      }
    );
  };

  /**
   * Function to add a week to a meal plan
   * @param {*} templateId
   */
  const handleAddWeek = (templateId) => {
    const body = {};
    const url = `/actions/meal-planner/add-week-meal-plan?mealPlanId=${templateId}&proxyAthlete=${undefined}`;

    submit(url, {
      method: "POST",
      body,
    });
  };

  return (
    <section className="meal-plan-templates-section">
      <div className="beta-container">
        <span className="beta-text">(Beta - New Features Coming Soon)</span>
      </div>
      <div className="meal-plan-templates">
        {sortedTemplates.map((template) => {
          const {
            weeks = [],
            title = "",
            description = "",
            _id: templateId,
          } = template;
          return (
            <article data-col="1/3" className="card" key={templateId}>
              <div>
                <div className="details">
                  <header className={`text ${heightClass}`}>
                    <span className="title">{title}</span>
                    <span className="subtitle">{description}</span>
                  </header>
                  <ul className="weeks">
                    {weeks.map((week, index) => {
                      const editUrl =
                        index === 0
                          ? `/coaching-portal/templates/edit-meal-plan?mealPlan=${templateId}`
                          : `/coaching-portal/templates/edit-meal-plan?mealPlan=${templateId}&week=${
                              index + 1
                            }`;

                      return (
                        <li key={`${index}-${templateId}`}>
                          <div className="text">
                            <div className="number">
                              <span>
                                {index + 1}/{weeks.length}
                              </span>
                            </div>
                            <div className="week">Week {index + 1}</div>
                          </div>
                          <div className="icons">
                            <a
                              role="button"
                              data-button-type="secondary"
                              data-icon
                              data-icon-only
                              className="edit"
                              href={`/actions/refresh?modal[type]=copy-meal-plan-to&templateId=${templateId}&week=${index}`}
                            >
                              <SvgIcon
                                src="/img/new-icons/copy-to.svg"
                                alt="edit"
                              />
                            </a>
                            <a
                              role="button"
                              data-button-type="secondary"
                              data-icon
                              data-icon-only
                              className="edit"
                              href={editUrl}
                            >
                              <SvgIcon
                                src="/img/new-icons/edit-pencil.svg"
                                alt="edit"
                              />
                            </a>
                            <details ref={dropdownRef} role="list" open>
                              <summary
                                onClick={(e) =>
                                  handleOptions(e, templateId, index)
                                }
                              >
                                <SvgIcon src="/img/new-icons/vuesax-linear-add-square.svg" />
                              </summary>
                            </details>
                            {openOptions?.templateId === templateId &&
                              openOptions?.weekIndex === index && (
                                <ul
                                  className="meal-plan-templates-options"
                                  role="listbox"
                                  style={{
                                    top: `${openOptions.position.top}px`,
                                    left: `${openOptions.position.left}px`,
                                  }}
                                >
                                  {cardListIcon.map(
                                    ({ src, alt, id, message }) => {
                                      return (
                                        <li key={`${templateId}-${id}`}>
                                          <a
                                            href={""}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              if (id === "delete-week") {
                                                handleDeleteWeek(
                                                  templateId,
                                                  index
                                                );
                                              } else if (
                                                id === "duplicate-week"
                                              ) {
                                                handleDuplicateWeek(
                                                  templateId,
                                                  index
                                                );
                                              }
                                              setOpenOptions(null);
                                            }}
                                          >
                                            <SvgIcon
                                              src={src}
                                              alt={alt}
                                              noFilterOnLight={
                                                id === "delete-week"
                                              }
                                            />
                                            {message}
                                          </a>
                                        </li>
                                      );
                                    }
                                  )}
                                </ul>
                              )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <footer className="footer">
                  <button
                    className="edit-card"
                    data-button-type="text"
                    data-icon
                    onClick={() => {
                      handleAddWeek(templateId);
                    }}
                  >
                    <img
                      alt="add"
                      src="/img/new-icons/edit-gradient-icon.svg"
                    />
                    <span className="font-semibold">Add Week</span>
                  </button>
                  <div className="icons">
                    {footerListIcon.map(({ src, alt, id, url, message }) => {
                      const finalUrl =
                        id === "copy-meal-plan-from" ||
                        id === "copy-meal-plan-to" ||
                        id === "meal-plan-settings"
                          ? `${url}&templateId=${templateId}`
                          : id === "preview-template"
                          ? `${url}?mealPlan=${templateId}&viewMode=true`
                          : url;

                      return (
                        <ToolTip
                          id={`${templateId}-${id}`}
                          body={message}
                          key={`${templateId}-${id}`}
                        >
                          <a
                            href={id === "delete-template" ? "" : finalUrl}
                            onClick={(e) => {
                              if (id === "delete-template") {
                                e.preventDefault();
                                handleDeleteTemplate(templateId);
                              }
                            }}
                          >
                            <div
                              className="bg-[--white-05] p-1 rounded-[--border-radius-md] hover:bg-[--white-20] cursor-pointer"
                              id="img-footer-cycle-card-container"
                            >
                              <SvgIcon
                                src={src}
                                alt={alt}
                                noFilterOnLight={id === "delete-template"}
                              />
                            </div>
                          </a>
                        </ToolTip>
                      );
                    })}
                  </div>
                </footer>
              </div>
            </article>
          );
        })}
      </div>
      <Pagination total={mealPlanCount} pageSize={40} />
    </section>
  );
};

MealPlanTemplates.propTypes = {
  locals: PropTypes.object,
  templates: PropTypes.array,
};

export default MealPlanTemplates;
